import React from 'react';
import PropTypes from 'prop-types';

import {Box, TextField} from 'views/components';
import {ArrowUpward} from 'views/icons';

import backend from 'utils/backend';

const ChatApp = (props) => {
  const [userInput, setUserInput] = React.useState('');
  const [isTyping, setIsTyping] = React.useState(false);
  const chatBodyRef = React.useRef(null);
  const wsRef = React.useRef(null);
  const userHasRespondedRef = React.useRef(false);

  const [messages, setMessages] = React.useState([
    {
      text: 'Hello! What would you like help with today?',
      isUser: false,
      timestamp: new Date().getTime(),
    },
  ]);

  const [leadId] = React.useState(() => {
    const key = 'assistantLeadId';
    let id = localStorage.getItem(key);
    if (!id) {
      id = Math.floor(1000000000 + Math.random() * 9000000000).toString();
      localStorage.setItem(key, id);
    }
    return id;
  });

  React.useEffect(() => {
    const onMessage = (data) => {
      if (data.message === 'Endpoint request timed out') {
        console.warn('Timeout message received, not adding to messages');
        return;
      }
      const botResponse = data.response;
      if (botResponse) {
        const newMessage = {
          text: botResponse,
          isUser: false,
          timestamp: new Date().getTime(),
        };
        setMessages((prev) => [...prev, newMessage]);
        setIsTyping(false);

        window.parent.postMessage(
            {type: 'NEW_BOT_MESSAGE', message: newMessage},
            '*',
        );
      }
    };

    const onOpen = () => console.info('WebSocket connected');
    const onError = (error) => console.error('WebSocket error:', error);
    const onClose = () => console.info('WebSocket closed');

    wsRef.current = new backend.AssistantChat.AssistantWebsocketManager(onMessage, onOpen, onError, onClose);

    wsRef.current.connect();

    window.parent.postMessage({type: 'IFRAME_READY', message: ''}, '*');

    // Reconnect on tab becoming visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const ws = wsRef.current?.ws;
        if (!ws || ws.readyState === WebSocket.CLOSED) {
          console.info('Reopening WebSocket after page became visible');
          wsRef.current?.connect();
        } else if (ws.readyState !== WebSocket.OPEN) {
        // Catch any half-dead states
          console.info('WebSocket not open, forcing reconnect');
          wsRef.current?.close();
          wsRef.current?.connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wsRef.current?.close();
    };
  }, [leadId]);

  React.useEffect(() => {
    window.parent.postMessage(
        {type: 'MESSAGES', messages},
        '*',
    );
  }, [messages]);

  const sendMessage = (text) => {
    const messageText = text || userInput.trim();
    if (!messageText) return;
    setMessages((prev) => [...prev, {text: messageText, isUser: true, timestamp: new Date().getTime()}]);
    setUserInput('');
    setIsTyping(true);
    wsRef.current?.send({action: 'sendMessage', message: messageText, sessionId: leadId, leadId});
    if (!userHasRespondedRef.current) {
      userHasRespondedRef.current = true;

      // If there is a source, update the source in the conversation
      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('source');
      if (source) {
        wsRef.current?.send({action: 'updateSource', source, sessionId: leadId});
      }
    }
  };

  const formatTimeAgo = (timestamp) => {
    const seconds = Math.floor((new Date().getTime() - timestamp) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  React.useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  // Listen for messages from iframe
  React.useEffect(() => {
    const handleMessage = (event) => {
      // Security?
      // if (event.origin !== 'https://carequilt.ca') return;
      const data = event.data;
      if (data?.type === 'NEW_USER_MESSAGE' && typeof data.message === 'string') {
        sendMessage(data.message);
      } else if (data?.type === 'INIT_MESSAGE' && typeof data.message === 'string') {
        // Only set the initial message if the user hasn't responded yet
        if (!userHasRespondedRef.current) {
          const splitMessages = data.message.split('\n\n\n').map((message) => {
            return {
              text: message,
              isUser: false,
              timestamp: new Date().getTime(),
            };
          });
          setMessages((e) => {
            return [
              ...splitMessages,
            ];
          });
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <>
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          margin: 0,
          background: 'white',
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: '100%',
            height: '100%',
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            boxSizing: 'border-box',
            margin: 0,
            padding: '20px 20px 0 20px',
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              overflowY: 'auto',
              padding: '20px 0',
              scrollbarWidth: 'none',
              position: 'relative',
              maxWidth: '100vw',
              overscrollBehavior: 'auto',
            }}
            ref={chatBodyRef}
          >
            {
              messages.map((message, index) => {
                const isLast = index === messages.length - 1;
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: message.isUser ? 'flex-end' : 'flex-start', // align left/right
                      width: '100%',
                      opacity: 1,
                      animation: 'fadeIn 0.5s ease forwards',
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '85%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: message.isUser ? 'flex-end' : 'flex-start', // keep timestamp aligned properly
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: message.isUser ? '#FC8301' : '#f8f9fa',
                          color: message.isUser ? 'white' : '#333',
                          paddingY: {xs: '12px', sm: '14px', md: '18px'}, // vertical padding
                          paddingX: {xs: '16px', sm: '20px', md: '24px'}, // horizontal padding (wider)
                          borderRadius: '28px',
                          fontSize: {xs: '14px', sm: '16px', md: '18px'},
                          lineHeight: 1.5,
                          whiteSpace: 'pre-line',
                          wordBreak: 'break-word', // breaks long words to wrap properly
                          overflowWrap: 'break-word', // ensures wrapping even in edge cases
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {message.text.replace(/\\n/g, '\n')}
                      </Box>
                      {
                        isLast && !message.isUser && (
                          <Box
                            sx={{
                              fontSize: '12px',
                              color: '#64748b',
                              marginTop: '4px',
                              marginLeft: '8px',
                              textAlign: 'left',
                            }}
                          >
                          CareQuilt • {formatTimeAgo(message.timestamp)}
                          </Box>
                        )
                      }
                    </Box>
                  </Box>
                );
              })
            }
          </Box>
          <Box
            sx={{
              paddingBottom: '20px',
              paddingTop: '4px',
              borderTop: '1px solid #eee',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {
              isTyping &&
                <Box
                  sx={{
                    display: 'flex',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    width: 'fit-content',
                    bottom: '100%',
                    left: '20px',
                    zIndex: 2,
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Box
                    sx={{color: '#64748b', fontSize: '14px'}}
                  >
                  CareQuilt is typing
                  </Box>
                  <Box
                    sx={{
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#64748b',
                      borderRadius: '50%',
                      // marginRight: "4px",
                      animation: 'typing 2s infinite ease-in-out',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#64748b',
                      borderRadius: '50%',
                      // marginRight: "4px",
                      animation: 'typing 2s infinite ease-in-out 0.2s',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#64748b',
                      borderRadius: '50%',
                      animation: 'typing 2s infinite ease-in-out 0.4s',
                    }}
                  />
                </Box>
            }
            <Box
              sx={{
                display: 'flex',
                gap: {xs: '6px', sm: '8px', md: '12px'},
                width: '100%',
              }}
            >
              <TextField
                variant="outlined"
                fullWidth
                multiline
                value={userInput}
                onChange={(e) => setUserInput(e)}
                placeholder="Type your message or question here..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (e.ctrlKey) {
                      // Insert a new line
                      const {selectionStart, selectionEnd} = e.target;
                      const newValue = userInput.substring(0, selectionStart) + '\n' + userInput.substring(selectionEnd);
                      setUserInput(newValue);

                      // Move cursor after newline
                      requestAnimationFrame(() => {
                        e.target.selectionStart = e.target.selectionEnd = selectionStart + 1;
                      });
                    } else {
                      e.preventDefault(); // Prevent newline
                      sendMessage(userInput);
                    }
                  }
                }}
                sx={{
                  '& textarea': {resize: 'none'}, // prevent resizing if you want
                }}
              />
              <Box
                component="button"
                onClick={() => sendMessage(userInput)}
                disabled={isTyping}
                sx={{
                  backgroundColor: '#FC8301',
                  color: 'white',
                  border: 'none',
                  padding: {
                    xs: '10px',
                    sm: '12px',
                    md: '16px',
                  },
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s',
                }}
              >
                <ArrowUpward />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <style>
        {`
          @keyframes typing {
            0%, 80%, 100% {
              transform: scale(0);
              opacity: 0.3;
            }
            40% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
    </>
  );
};

ChatApp.propTypes = {
  initalMessage: PropTypes.string,
  emptyInitalMessage: PropTypes.func,
};

export default ChatApp;
